import React from 'react';
import './PrintableOrderAppendix.css'; // We will create this CSS file next

// Interface definitions will be added below

interface PrintableOrderAppendixProps {
    order: Order; // We'll define Order below
    companySettings: Settings; // Use Settings interface, defined below
}

// Define interfaces locally based on provided JSON and existing usage
// Based on SettingsContext.tsx and potential backend model
interface Settings {
  name: string; // From context
  director?: string | null;
  basisOfAuthority?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  bin?: string | null; // From context (assuming it maps from taxId if needed)
  taxId?: string | null; // From context
  iik?: string | null; // From context (assuming it maps from bankAccount if needed)
  bankAccount?: string | null; // From context
  bankName?: string | null; // From context
  bik?: string | null; // From context (assuming it maps from bankBik if needed)
  bankBik?: string | null; // From context
  logo?: string | null; // From context
  vatIncluded?: boolean; // From context or default? Check Settings logic
  // Add other fields as needed from CompanySettings/Settings model
}

// Based on provided JSON
interface Structure {
  id: number;
  code: string; // Use code instead of name?
  name?: string; // Keep if needed elsewhere, but JSON has code/description
  type: string;
  location: string;
  description?: string; // Available in JSON
  isLED?: boolean;
}

interface OrderItem {
  id: number;
  orderId: number;
  structureId: number;
  Structure: Structure; // Nested object name from JSON
  startDate: string | Date;
  endDate: string | Date;
  price?: number; // Keep for calculation fallback?
  unitPrice: number; // Use unitPrice from JSON
  quantity: number;
  isMonthlyRate: boolean;
  discountPercentage: number | null;
  totalPrice: number;
  finalPrice: number;
  notes?: string;
  status?: string;
}

interface Client {
  id: number;
  name: string;
  director?: string; // Use director from JSON
  phone?: string;
  email?: string;
  address?: string;
  bin?: string; // Not in provided Client JSON, but used in template
  iik?: string; // Not in provided Client JSON, but used in template
  taxId?: string; // Available in JSON
  // Add other relevant client fields: bik, kguCode, kbe, okpo if needed
}

// Contract interface remains the same as it's potentially populated separately
interface Contract {
  _id?: string;
  id?: number;
  contractNumber?: string;
  contractDate?: string | Date;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  Client: Client; // Nested object name from JSON
  status: string;
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paidAmount: number;
  orderDate: string | Date;
  startDate: string | Date;
  endDate: string | Date;
  paymentTerms?: string;
  notes?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  contractId?: number | null; // Keep as potentially populated
  contract?: Contract; // Keep as potentially populated
  items?: OrderItem[]; // Keep for compatibility if OrderAppendixPrintPage maps to it
  OrderItems: OrderItem[]; // Use OrderItems from JSON
  // Payments?: Payment[]; // Add if needed
  // CreatedBy?: CreatedByUser; // Add if needed
}


const PrintableOrderAppendix: React.FC<PrintableOrderAppendixProps> = ({ order, companySettings }) => {
    const formatDate = (dateString: string | Date) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    // Use OrderItems from the Order object
    const orderItems = order.OrderItems || order.items || [];

    const calculateTotal = (): number => {
        // Calculate total based on finalPrice from OrderItem JSON
        return orderItems.reduce((sum: number, item: OrderItem) => {
            // Use finalPrice if available and is a number, otherwise default to 0
            const itemFinalPrice = typeof item.finalPrice === 'number' ? item.finalPrice : 0;
            return sum + itemFinalPrice;
        }, 0);
    };

    const totalAmount = calculateTotal(); // Recalculate based on finalPrice sum

    // Function to convert number to words (Kazakh specific - needs implementation or library)
    // Placeholder function
    const numberToWordsKz = (num: number): string => {
        // This needs a proper implementation for Kazakh number-to-words conversion
        return `${num.toLocaleString('kk-KZ')} (словами тенге)`; // Placeholder
    };

    console.log('Order:', order); // Debugging line to check order data

    return (
        <div className="printable-order-appendix-container">
            <div className="appendix-header">
                <h2>Приложение №1</h2>
                {/* Access contract details if available */}
                <p>к Договору № {order.contract?.contractNumber || '_____'} от «{order.contract?.contractDate ? formatDate(order.contract.contractDate) : '__'}» ______ {new Date(order.contract?.contractDate || order.orderDate || Date.now()).getFullYear()} г.</p>
            </div>

            <div className="appendix-body">
                <p>В соответствии с условиями Договора, Исполнитель по поручению Заказчика оказывает Работы/Услуги по Размещению Рекламных материалах на Рекламных носителях.</p>
                <p>Период – с {formatDate(order.startDate)} г. – по {formatDate(order.endDate)} г.</p>
                {/* Use Client object from Order */}
                <p>Бренд – «{order.Client?.name || 'Не указан'}»</p>

                <h3>1. Дислокация объектов визуальной рекламы:</h3>
                 {/* Iterate over orderItems */}
                {orderItems.map((item: OrderItem, index: number) => (
                     // Use item.id from JSON as key
                     // Use Structure object from OrderItem
                     // Display Structure code or description or name
                     <p key={item.id}>{index + 1}. {item.Structure?.code || item.Structure?.description || item.Structure?.name || 'Конструкция не указана'} - {item.Structure?.location || ''}</p>
                ))}


                <table className="items-table">
                    <thead>
                        <tr>
                            <th>Наименование</th>
                            <th>Номера Конструкций</th>
                            <th>Цена</th>
                            <th>Кол-во сторон</th>
                            <th>Период</th>
                            <th>Итого</th>
                        </tr>
                    </thead>
                    <tbody>
                         {/* Iterate over orderItems */}
                        {orderItems.map((item: OrderItem, index: number) => {
                             // Calculate duration based on item's dates
                             const days = Math.ceil((new Date(item.endDate).getTime() - new Date(item.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1;
                             // Use unitPrice from JSON for 'Цена'
                             const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
                             // Use finalPrice from JSON for 'Итого'
                             const finalItemPrice = typeof item.finalPrice === 'number' ? item.finalPrice : 0;
                             const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                             return (
                                 // Use item.id as key
                                <tr key={item.id}>
                                     {/* Use Structure object from OrderItem */}
                                    <td>{item.Structure?.type || 'Не указано'}</td>
                                    <td>{item.Structure?.code || item.Structure?.name || 'Не указано'}</td>
                                    <td>{unitPrice.toLocaleString('kk-KZ', { minimumFractionDigits: 2 })}</td>
                                    <td>{quantity}</td>
                                    <td>{days} дней</td>
                                    <td>{finalItemPrice.toLocaleString('kk-KZ', { minimumFractionDigits: 2 })}</td>
                                </tr>
                             )
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>ИТОГО ОБЩАЯ СУММА:</td>
                             {/* Use finalAmount from Order object if available, otherwise use calculated total */}
                            <td style={{ fontWeight: 'bold' }}>{(typeof order.finalAmount === 'number' ? order.finalAmount : totalAmount).toLocaleString('kk-KZ', { minimumFractionDigits: 2 })}</td>
                        </tr>
                    </tfoot>
                </table>

                {/* TODO: Implement numberToWordsKz properly */}
                 {/* Use finalAmount from Order object if available */}
                <h3>2. Общая стоимость Работ/Услуг по Приложению №1 составляет {(typeof order.finalAmount === 'number' ? order.finalAmount : totalAmount).toLocaleString('kk-KZ', { minimumFractionDigits: 2 })} ({numberToWordsKz(typeof order.finalAmount === 'number' ? order.finalAmount : totalAmount)}) тенге {companySettings.vatIncluded ? 'с учетом НДС' : 'без учета НДС'}.</h3>

                <h3>3. Оплата платежей за Работы/Услуги производится в национальной валюте Республики Казахстан – тенге, путем безналичного расчета на банковский расчетный счет Исполнителя, указанный в Договоре.</h3>

                <h3>4. Оплата производится в следующем порядке: Заказчик осуществляет оплату оказанных Услуг в течение 3 (трех) рабочих дней со дня предоставления Исполнителем счета на оплату или счета-фактуры оформленного в соответствии с требованиями законодательства Республики Казахстан.</h3>

                <h3>5. Любые изменения и дополнения к настоящему Приложению действительны при условии, если они совершены в письменной форме и подписаны сторонами либо надлежаще уполномоченными на то представителями Сторон.</h3>

                <h3>6. Настоящее Приложение является неотъемлемой частью Настоящего Договора.</h3>

                {/* Optional clauses from docx - adjust as needed */}
                {/* <h3>7. Каждая последующая смена ролика 10 000 тенге (вне зависимости от ЛЭД-а)</h3> */}
                {/* <h3>8. Фото/видео отчет ...</h3> */}
                {/* <h3>9. Налог на наружную рекламу оплачивается Исполнителем.</h3> */}

            </div>

            <div className="appendix-footer">
                <div className="party-details">
                    <h4>Исполнитель:</h4>
                    <p><strong>{companySettings.name}</strong></p>
                    <p>{companySettings.address}</p>
                    <p>БИН {companySettings.bin || companySettings.taxId}</p>
                    <p>ИИК {companySettings.iik || companySettings.bankAccount}</p>
                    <p>{companySettings.bankName}</p>
                    <p>БИК {companySettings.bik || companySettings.bankBik}</p>
                    <p>E-mail: {companySettings.email}</p>
                    <p>Контактный номер: {companySettings.phone}</p>
                    <br/>
                    <p>Директор</p>
                    <br/>
                    <p>_______________________ /{companySettings.director || '_______________'}/</p>
                </div>
                <div className="party-details">
                    <h4>Заказчик:</h4>
                     {/* Use Client object from Order */}
                    <p><strong>{order.Client?.name || 'Не указано'}</strong></p>
                    <p>{order.Client?.address || 'Адрес не указан'}</p>
                     {/* Use taxId if bin is missing, or show placeholder */}
                    <p>БИН {order.Client?.bin || order.Client?.taxId || 'Не указан'}</p>
                    <p>ИИК {order.Client?.iik || 'Не указан'}</p>
                    {/* Add other client details if needed and available */}
                    {/* <p>БИК {order.Client?.bik || 'Не указан'}</p> */}
                    {/* <p>Код КГУ {order.Client?.kguCode || 'Не указан'}</p> */}
                    {/* <p>КБЕ {order.Client?.kbe || 'Не указан'}</p> */}
                    {/* <p>ОКПО {order.Client?.okpo || 'Не указан'}</p> */}
                    {/* <p>ГУ Комитет казначейства МФ РК</p> */}
                    <p>E-mail: {order.Client?.email || 'Не указан'}</p>
                    <p>Тел: {order.Client?.phone || 'Не указан'}</p>
                    <br/>
                    <p>Директор</p>
                    <br/>
                     {/* Use director */}
                    <p>______________/ {order.Client?.director || '________________'}/</p>
                </div>
            </div>
        </div>
    );
};

export default PrintableOrderAppendix;
